import { createBrowserRouter } from 'react-router-dom';
import { MobXProviderContext } from 'mobx-react';
import ReactDOM from 'react-dom/client';
import React, { useMemo } from 'react';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import { createAppTheme } from '@styles/theme';

import { App } from './App';
import { initializeDependenciesAndCreateStores, Stores } from './createStores';
import { routes } from './routes/AppRouter';
import { ThemeModeProvider, useThemeMode } from './ThemeContext';


declare module '@mui/material/styles' {
    // interface DefaultTheme extends Theme {}
    type ThemeProps = {
        colors: {
            black: string,
            white: string,
            red: string,
            green: string,
            yellow: string,
            red_transparent: string,
            deep_red: string,
            transp_yellow_hightlight: string,
            violet_darkest: string,
            violet_darker: string,
            violet_primary: string,
            deep_blue: string,
            tab_white: string,
            blue_link: string,
            blue_primary: string,
            blue_neon: string,
            blue_neon_luminating: string,
            grey_text: string,
            grey_darker: string,
            grey_light: string,
            grey_background: string,
            transp_violet: string,
            transp_grey_light: string,
            grey_lighter: string,
            icon_background: string,
            transp_blue_darker: string,
            transp_blue_disabled: string,
            transp_blue: string,
            text_heading: string,
            text_muted_purple: string,
            text_subtle_purple: string,
            surface_elevated: string,
            surface_page: string,
            surface_alt: string,
            border_subtle: string,
            error_bg: string,
            error_text_dark: string,
            indigo_primary: string,
            teal_accent: string,
            tree_node_bg: string,
            tree_node_accent: string,
        },
        font: {
            primary: string,
            secondary: string,
        },
        spacing: Function,
        radius: number,
        screens: {
            mobile: number,
            tablet: number,
            desktop: number,
            wide: number,
        },
        up: (breakpoint: 'mobile' | 'tablet' | 'desktop' | 'wide') => string,
        down: (breakpoint: 'mobile' | 'tablet' | 'desktop' | 'wide') => string,
        between: (
            from: 'mobile' | 'tablet' | 'desktop' | 'wide',
            to: 'mobile' | 'tablet' | 'desktop' | 'wide'
        ) => string,
    }

    interface ThemeOptions {
        colors?: {
            black?: string,
            white?: string,
            red?: string,
            red_transparent?: string,
            deep_red?: string,
            green?: string,
            yellow?: string,
            transp_yellow_hightlight?: string,
            violet_darkest?: string,
            violet_darker?: string,
            violet_primary?: string,
            deep_blue?: string,
            tab_white?: string,
            blue_link?: string,
            blue_primary?: string,
            blue_neon?: string,
            blue_neon_luminating?: string,
            grey_text?: string,
            grey_darker?: string,
            grey_light?: string,
            grey_background?: string,
            transp_violet?: string,
            transp_grey_light?: string,
            grey_lighter?: string,
            icon_background?: string,
            transp_blue_darker?: string,
            transp_blue_disabled?: string,
            transp_blue?: string,
            text_heading?: string,
            text_muted_purple?: string,
            text_subtle_purple?: string,
            surface_elevated?: string,
            surface_page?: string,
            surface_alt?: string,
            border_subtle?: string,
            error_bg?: string,
            error_text_dark?: string,
            indigo_primary?: string,
            teal_accent?: string,
            tree_node_bg?: string,
            tree_node_accent?: string,
        },
        font?: {
            primary?: string,
            secondary?: string,
        },
        spacing?: Function,
        radius?: number,
        up?: (breakpoint: 'mobile' | 'tablet' | 'desktop' | 'wide') => string,
        down?: (breakpoint: 'mobile' | 'tablet' | 'desktop' | 'wide') => string,
        between?: (
            from: 'mobile' | 'tablet' | 'desktop' | 'wide',
            to: 'mobile' | 'tablet' | 'desktop' | 'wide'
        ) => string,
        touch?: string,
    }
}


const rootHtmlElement = document.getElementById('root');
const root = ReactDOM.createRoot(rootHtmlElement!);

type AppWrapperProps = { stores: Stores; router: any };

const AppWrapper: React.FC<AppWrapperProps> = ({ stores, router }) => {
    const { mode } = useThemeMode();
    const theme = useMemo(() => createTheme(createAppTheme(mode) as any), [mode]);

    return (
        <MobXProviderContext.Provider value={stores}>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <App router={router} />
                </ThemeProvider>
            </StyledEngineProvider>
        </MobXProviderContext.Provider>
    );
};

const renderApp = (stores: Stores, router: any) => (
    root.render(
        <ThemeModeProvider>
            <AppWrapper stores={stores} router={router} />
        </ThemeModeProvider>,
    )
);

const renderAppBootstrapError = () => (
    root.render(
        <div>ERROR - app bootsrap failed</div>,
    )
);

(async () => {

    const router = createBrowserRouter(routes);

    const navigate = router.navigate;

    try {
        const initialStores = await initializeDependenciesAndCreateStores({ navigate });
        // Was using { basename: '/' } as parameter for createBrowserHistory
        // navigate('/');

        renderApp(initialStores, router);
    } catch {
        renderAppBootstrapError();
    }
})();

if (module.hot) {
    module.hot.accept();
}
